import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Crown, Medal, Sparkles, Trophy, Star, Zap } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { LeaderboardUser } from '@/types/leaderboard';
import confetti from 'canvas-confetti';

interface LeaderboardPodiumProps {
  topThree: LeaderboardUser[];
  showPodium: boolean;
  handleUserClick: (user: LeaderboardUser) => void;
}

const LeaderboardPodium: React.FC<LeaderboardPodiumProps> = ({ 
  topThree = [], 
  showPodium, 
  handleUserClick 
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const navigate = useNavigate();
  
  // Use default empty array and ensure we handle cases with fewer than 3 users
  const safeTopThree = topThree || [];
  const first = safeTopThree[0] || null;
  const second = safeTopThree[1] || null;
  const third = safeTopThree[2] || null;
  
  // Navigate to profile with delay after toast
  const handleProfileClick = (user: LeaderboardUser) => {
    handleUserClick(user);
    // Navigate after showing toast
    setTimeout(() => {
      navigate(`/profile/${user.id}`);
    }, 300);
  };
  
  // Only show confetti if we actually have data to display
  useEffect(() => {
    if (showPodium && first) {
      // Trigger confetti after a slight delay
      const timer = setTimeout(() => {
        setShowConfetti(true);
        // Fire confetti from the center-top
        confetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.2, x: 0.5 }
        });
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [showPodium, first]);
  
  if (!first) {
    return null; // Don't render the podium if there's no first place
  }
  
  return (
    <div className="mb-12 relative overflow-hidden rounded-xl">
      <div className="bg-gradient-to-br from-indigo-600/80 via-purple-600/80 to-pink-600/80 p-8 rounded-xl shadow-xl border-2 border-white/10">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold flex items-center justify-center gap-2 text-white">
            <Trophy className="h-7 w-7 text-yellow-300" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-yellow-500 to-blue-500">
              TOP PERFORMERS
            </span>
            <Sparkles className="h-6 w-6 text-yellow-400" />
          </h2>
        </div>
        
        {/* Podium */}
        <div className="flex justify-center items-end gap-4 mx-auto max-w-3xl">
          {/* Second Place */}
          {second && (
            <motion.div
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 50 }}
              animate={showPodium ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="flex flex-col items-center">
                <Medal className="h-8 w-8 text-slate-300 mb-2" />
                
                <div 
                  className="relative cursor-pointer"
                  onClick={() => handleProfileClick(second)}
                >
                  <Avatar className="h-20 w-20 border-4 border-slate-300 bg-gradient-to-r from-blue-400 to-cyan-300 mb-2 hover:shadow-md hover:shadow-blue-400/50 transition-all">
                    <AvatarImage src={second.avatar || '/placeholder.svg'} alt={second.name || 'User'} />
                    <AvatarFallback className="bg-gradient-to-r from-blue-400 to-cyan-300 text-white font-bold">
                      {second.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full border-2 border-white h-7 w-7 flex items-center justify-center text-white font-bold text-xs">
                    2
                  </div>
                </div>
                
                <div className="bg-gradient-to-b from-blue-500 to-blue-600 border-2 border-blue-300 p-3 w-32 text-center rounded-lg">
                  <p className="font-bold text-sm text-white truncate">{second.name || 'User'}</p>
                  <div className="flex items-center justify-center mt-1">
                    <Star className="h-4 w-4 text-yellow-300 mr-1" />
                    <p className="text-xl font-bold text-white">{second.score || 0}</p>
                  </div>
                  <div className="mt-1 bg-blue-900/50 px-2 py-0.5 rounded-full text-xs text-blue-100 inline-flex items-center">
                    <Zap className="h-3 w-3 mr-1 text-blue-200" />
                    Level {second.level || 1}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* First Place */}
          <motion.div
            className="flex flex-col items-center -mt-10 z-10"
            initial={{ opacity: 0, y: 50 }}
            animate={showPodium ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
            transition={{ duration: 0.5, delay: 0 }}
          >
            <div className="flex flex-col items-center">
              <Crown className="h-12 w-12 text-yellow-400 mb-2" />
              
              <div 
                className="relative cursor-pointer"
                onClick={() => handleProfileClick(first)}
              >
                <Avatar className="h-28 w-28 border-4 border-yellow-400 bg-gradient-to-r from-amber-400 to-yellow-500 mb-2 hover:shadow-lg hover:shadow-yellow-400/50 transition-all">
                  <AvatarImage src={first.avatar || '/placeholder.svg'} alt={first.name || 'User'} />
                  <AvatarFallback className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white font-bold text-2xl">
                    {first.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                
                <div className="absolute -bottom-2 -right-2 bg-amber-500 rounded-full border-2 border-white h-9 w-9 flex items-center justify-center text-white font-bold text-lg">
                  1
                </div>
              </div>
              
              <div className="bg-gradient-to-b from-amber-500 to-orange-600 border-2 border-yellow-300 p-3 w-40 text-center rounded-lg">
                <p className="font-bold text-lg text-white truncate">{first.name || 'User'}</p>
                <div className="flex items-center justify-center mt-1">
                  <Trophy className="h-5 w-5 text-yellow-300 mr-1" />
                  <p className="text-2xl font-bold text-white">{first.score || 0}</p>
                </div>
                <div className="mt-2 bg-gradient-to-r from-amber-600 to-red-600 px-3 py-1 rounded-full text-sm text-yellow-100 font-bold inline-flex items-center">
                  <Crown className="h-4 w-4 mr-1 text-yellow-300" />
                  Champion
                </div>
                <div className="mt-1 bg-amber-900/50 px-2 py-0.5 rounded-full text-xs text-amber-100 inline-flex items-center">
                  <Zap className="h-3 w-3 mr-1 text-amber-200" />
                  Level {first.level || 1}
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Third Place */}
          {third && (
            <motion.div
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 50 }}
              animate={showPodium ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="flex flex-col items-center">
                <Medal className="h-8 w-8 text-amber-700 mb-2" />
                
                <div
                  className="relative cursor-pointer"
                  onClick={() => handleProfileClick(third)}
                >
                  <Avatar className="h-20 w-20 border-4 border-amber-700 bg-gradient-to-r from-purple-500 to-pink-500 mb-2 hover:shadow-md hover:shadow-purple-400/50 transition-all">
                    <AvatarImage src={third.avatar || '/placeholder.svg'} alt={third.name || 'User'} />
                    <AvatarFallback className="bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold">
                      {third.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="absolute -bottom-1 -right-1 bg-purple-500 rounded-full border-2 border-white h-7 w-7 flex items-center justify-center text-white font-bold text-xs">
                    3
                  </div>
                </div>
                
                <div className="bg-gradient-to-b from-purple-500 to-purple-700 border-2 border-purple-300 p-3 w-32 text-center rounded-lg">
                  <p className="font-bold text-sm text-white truncate">{third.name || 'User'}</p>
                  <div className="flex items-center justify-center mt-1">
                    <Star className="h-4 w-4 text-yellow-300 mr-1" />
                    <p className="text-xl font-bold text-white">{third.score || 0}</p>
                  </div>
                  <div className="mt-1 bg-purple-900/50 px-2 py-0.5 rounded-full text-xs text-purple-100 inline-flex items-center">
                    <Zap className="h-3 w-3 mr-1 text-purple-200" />
                    Level {third.level || 1}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPodium;
